/**
 * CoreDepositFeedPage -- the FI operator's read of the core deposit feed
 * (GH #2911, LOI section 2.2): the age of the last accepted delivery, every
 * delivery the institution's core has sent, and the provenance attestation
 * over a business-date window.
 *
 * Route: /core-deposit-feed. It sits outside /tempo on purpose. The Tempo nav
 * group requires `settings:update`, which only FI_ADMIN carries, while the
 * feed's reads are open to every FEED_READ_ROLES role (fi_admin,
 * fi_loan_officer, fi_compliance, fi_ops; services/tempo-engine/src/ingestion/
 * routes.ts). The route itself carries no permission gate, so the server's
 * role check is the one that decides.
 *
 * Reads (core-deposit-feed.openapi.yaml):
 *   GET /api/v1/core-deposit-feed/deliveries               listFeedDeliveries
 *   GET /api/v1/core-deposit-feed/provenance-attestation   getProvenanceAttestation
 *
 * Read-only. The write operations (open, submit, seal, channel binding) belong
 * to the adapter path and have no control on this screen.
 *
 * AN ABSENCE STATE RENDERS ONLY WHEN THE READ SUCCEEDED. A failed read renders
 * its error and no empty state, the AccountDecisionPage convention.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  BodyText,
  Button,
  Card,
  EmptyState,
  Heading,
  Input,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@aaimplatform/ui';
import {
  useFeedDeliveries,
  useLastAcceptedDelivery,
  useProvenanceAttestation,
  type DeliveryStatus,
  type FeedDelivery,
  type FeedDeliveryFilters,
} from '../../hooks/useCoreDepositFeed';
import {
  ABSENT,
  DeliveryStatusBadge,
  FieldRow,
  SURFACE_DARK_DESCRIPTION,
  formatAge,
  formatCount,
  formatDate,
  formatDateTime,
} from './FeedParts';

const STATUS_OPTIONS: ReadonlyArray<{ value: '' | DeliveryStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'OPEN' },
  { value: 'ACCEPTED', label: 'ACCEPTED' },
  { value: 'QUARANTINED', label: 'QUARANTINED' },
  { value: 'SUPERSEDED', label: 'SUPERSEDED' },
];

/** Days in the default attestation window. A screen default, not a contract value. */
const DEFAULT_WINDOW_DAYS = 30;

/** A UTC calendar date, `days` before `nowMs`, as YYYY-MM-DD. */
function utcDateDaysAgo(nowMs: number, days: number): string {
  return new Date(nowMs - days * 86_400_000).toISOString().slice(0, 10);
}

export function deliveryDetailPath(deliveryId: string): string {
  return `/core-deposit-feed/deliveries/${encodeURIComponent(deliveryId)}`;
}

function ContinuityCard({ nowMs }: { nowMs: number }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useLastAcceptedDelivery();
  const delivery = data?.delivery ?? null;

  return (
    <Card className="p-4">
      <Heading level={3} size="card">Last accepted delivery</Heading>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        The age is measured from receivedAt, the instant tempo-engine recorded the delivery,
        to this browser&apos;s clock.
      </BodyText>
      <div className="mt-3">
        {isLoading && <Skeleton className="h-24 w-full" />}
        {error && (
          <Alert type="error" title="Last delivery load failed">
            {error.message}
          </Alert>
        )}
        {!isLoading && !error && data?.surfaceDark && (
          <EmptyState title="Feed not available" description={SURFACE_DARK_DESCRIPTION} />
        )}
        {!isLoading && !error && data && !data.surfaceDark && !delivery && (
          <EmptyState
            title="No accepted delivery"
            description="tempo-engine has recorded no ACCEPTED delivery for this institution."
          />
        )}
        {delivery && (
          <div>
            <FieldRow label="age" value={formatAge(delivery.receivedAt, nowMs)} />
            <FieldRow label="receivedAt" value={formatDateTime(delivery.receivedAt)} />
            <FieldRow label="businessDate" value={formatDate(delivery.manifest.businessDate)} />
            <FieldRow label="coreSystemId" value={delivery.manifest.coreSystemId} />
            <FieldRow label="fileId" value={delivery.manifest.fileId} />
            <div className="mt-3">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate(deliveryDetailPath(delivery.deliveryId));
                }}
              >
                Open delivery
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function DeliveriesCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'' | DeliveryStatus>('');
  const [businessDateFrom, setBusinessDateFrom] = useState('');
  const [businessDateTo, setBusinessDateTo] = useState('');

  const filters = useMemo(() => {
    const next: Omit<FeedDeliveryFilters, 'cursor' | 'limit'> = {};
    if (status) next.status = status;
    if (businessDateFrom) next.businessDateFrom = businessDateFrom;
    if (businessDateTo) next.businessDateTo = businessDateTo;
    return next;
  }, [status, businessDateFrom, businessDateTo]);

  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useFeedDeliveries(filters);

  const deliveries = data?.pages.flatMap((page) => page.items) ?? [];
  const surfaceDark = data?.pages[0]?.surfaceDark ?? false;

  const columns: TableColumn<FeedDelivery>[] = [
    {
      key: 'status',
      label: 'Status',
      render: (_value, row) => <DeliveryStatusBadge status={row.status} />,
    },
    {
      key: 'businessDate',
      label: 'Business date',
      render: (_value, row) => <span className="font-mono">{formatDate(row.manifest.businessDate)}</span>,
    },
    {
      key: 'receivedAt',
      label: 'Received',
      render: (_value, row) => formatDateTime(row.receivedAt),
    },
    {
      key: 'coreSystemId',
      label: 'Core',
      render: (_value, row) => <span className="font-mono">{row.manifest.coreSystemId}</span>,
    },
    {
      key: 'restatementIntent',
      label: 'Intent',
      render: (_value, row) => <span className="font-mono">{row.manifest.restatementIntent}</span>,
    },
    {
      key: 'declaredRecordCount',
      label: 'Declared records',
      align: 'right',
      render: (_value, row) => formatCount(row.manifest.declaredRecordCount),
    },
    {
      key: 'outcome',
      label: 'Reconciliation',
      render: (_value, row) => (
        <span className="font-mono">{row.reconciliation?.outcome ?? ABSENT}</span>
      ),
    },
  ];

  return (
    <Card className="p-4">
      <Heading level={3} size="card">Deliveries</Heading>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        Newest received first. Select a row to read its manifest, reconciliation and landed records.
      </BodyText>
      <div className="flex flex-wrap items-end gap-4 mt-3">
        <Select
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value as '' | DeliveryStatus)}
          options={STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          fullWidth={false}
        />
        <Input
          label="Business date from"
          type="date"
          value={businessDateFrom}
          onChange={(event) => setBusinessDateFrom(event.target.value)}
          fullWidth={false}
        />
        <Input
          label="Business date to"
          type="date"
          value={businessDateTo}
          onChange={(event) => setBusinessDateTo(event.target.value)}
          fullWidth={false}
        />
      </div>
      <div className="mt-4">
        {isLoading && <Skeleton className="h-48 w-full" />}
        {error && (
          <Alert type="error" title="Deliveries load failed">
            {error.message}
          </Alert>
        )}
        {!isLoading && !error && surfaceDark && (
          <EmptyState title="Feed not available" description={SURFACE_DARK_DESCRIPTION} />
        )}
        {!isLoading && !error && !surfaceDark && deliveries.length === 0 && (
          <EmptyState
            title="No deliveries"
            description="tempo-engine has recorded no delivery that matches these filters."
          />
        )}
        {deliveries.length > 0 && (
          <>
            <Table
              columns={columns}
              data={deliveries}
              hoverable
              onRowClick={(row) => {
                void navigate(deliveryDetailPath(row.deliveryId));
              }}
            />
            {hasNextPage && (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  disabled={isFetchingNextPage}
                  onClick={() => {
                    void fetchNextPage();
                  }}
                >
                  {isFetchingNextPage ? 'Loading' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function AttestationCard({ nowMs }: { nowMs: number }) {
  const [windowFrom, setWindowFrom] = useState(() => utcDateDaysAgo(nowMs, DEFAULT_WINDOW_DAYS));
  const [windowTo, setWindowTo] = useState(() => utcDateDaysAgo(nowMs, 0));
  const { data: attestation, isLoading, error } = useProvenanceAttestation(windowFrom, windowTo);

  return (
    <Card className="p-4">
      <Heading level={3} size="card">Provenance attestation</Heading>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        Counts of core-direct and non-core-direct records over the business-date window, with
        the deliveries that quarantined or were still open at window close.
      </BodyText>
      <div className="flex flex-wrap items-end gap-4 mt-3">
        <Input
          label="Window from"
          type="date"
          value={windowFrom}
          onChange={(event) => setWindowFrom(event.target.value)}
          fullWidth={false}
        />
        <Input
          label="Window to"
          type="date"
          value={windowTo}
          onChange={(event) => setWindowTo(event.target.value)}
          fullWidth={false}
        />
      </div>
      <div className="mt-4">
        {(!windowFrom || !windowTo) && (
          <BodyText variant="ui" className="text-sm text-secondary">
            The attestation operation requires both window bounds.
          </BodyText>
        )}
        {isLoading && <Skeleton className="h-40 w-full" />}
        {error && (
          <Alert type="error" title="Attestation load failed">
            {error.message}
          </Alert>
        )}
        {!isLoading && !error && windowFrom && windowTo && attestation === null && (
          <EmptyState title="Feed not available" description={SURFACE_DARK_DESCRIPTION} />
        )}
        {attestation && (
          <div>
            <FieldRow label="coreDirectRecordCount" value={formatCount(attestation.coreDirectRecordCount)} />
            <FieldRow label="nonCoreDirectRecordCount" value={formatCount(attestation.nonCoreDirectRecordCount)} />
            <FieldRow label="coreOnly" value={attestation.coreOnly ? 'Yes' : 'No'} />
            <FieldRow label="quarantinedDeliveryCount" value={formatCount(attestation.quarantinedDeliveryCount)} />
            <FieldRow label="unsealedDeliveryCount" value={formatCount(attestation.unsealedDeliveryCount)} />
            <FieldRow label="dataComplete" value={attestation.dataComplete ? 'Yes' : 'No'} />
            <FieldRow
              label="coreSystemIds"
              value={attestation.coreSystemIds.length > 0 ? attestation.coreSystemIds.join(', ') : ABSENT}
            />
            <FieldRow
              label="adapterIds"
              value={attestation.adapterIds.length > 0 ? attestation.adapterIds.join(', ') : ABSENT}
            />
            <FieldRow label="generatedAt" value={formatDateTime(attestation.generatedAt)} />
            <FieldRow label="evidenceSha256" value={attestation.evidenceSha256 ?? ABSENT} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function CoreDepositFeedPage() {
  // One clock reading per mount, so the age and the default window agree.
  const [nowMs] = useState(() => Date.now());

  return (
    <div className="space-y-6">
      <div>
        {/* Level 2: PortalLayout's AppHeader renders the page h1. */}
        <Heading level={2} size="page">Core Deposit Feed</Heading>
        <BodyText variant="ui" className="text-sm text-secondary mt-1">
          Deliveries from the institution&apos;s core, how each reconciled against its own
          control totals, and where the landed records came from.
        </BodyText>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <ContinuityCard nowMs={nowMs} />
        <AttestationCard nowMs={nowMs} />
      </div>
      <DeliveriesCard />
    </div>
  );
}
